import Intercom from '@intercom/messenger-js-sdk';
import {useRouter} from '@tanstack/react-router';
import {useEffect} from 'react';
import ReactGA from 'react-ga4';
import {useTolgee} from 'tolgee';

import {useCurrentProjectQuery} from '~api/projects.queries';
import {isInternalEmail} from '~utils/miscUtils';
import {useAnalytics} from '~hooks/analytics.hooks';
import {ReduxStateUser} from '~redux/types/user';
import {selectUser} from '~redux/reducers/userReducer';
import {useAppSelector} from '~redux/index';

const deploymentEnv = import.meta.env.VITE_DEPLOYMENT_ENV ?? '';
const isE2eBuild = import.meta.env.VITE_E2E === 'true';
const includeTelemetry = !isE2eBuild && deploymentEnv === 'production';

function getLaunchDarklyClientID() {
  switch (deploymentEnv) {
    case 'production':
      return import.meta.env.VITE_LAUNCHDARKLY_CLIENT_ID_PROD;
    case 'staging':
      return import.meta.env.VITE_LAUNCHDARKLY_CLIENT_ID_STAGING;
    case 'development':
    default:
      return import.meta.env.VITE_LAUNCHDARKLY_CLIENT_ID_DEV;
  }
}

export const launchDarklyClientID = getLaunchDarklyClientID();

function getGoogleAnalyticsMeasurementID() {
  if (!includeTelemetry) {
    return null;
  }

  switch (deploymentEnv) {
    case 'production':
      return import.meta.env.VITE_GA_MEASUREMENT_ID_PROD;
    default:
      return null;
  }
}

const googleAnalyticsMeasurementID = getGoogleAnalyticsMeasurementID();

const setupGoogleAnalytics = (user?: ReduxStateUser) => {
  if (!user) return;

  const customer = user.customer;
  const projectID = user.currentProjectId;
  const userID = user.userId;
  const userEmail = user.email;

  if (googleAnalyticsMeasurementID && !isInternalEmail(userEmail)) {
    ReactGA.initialize([{trackingId: googleAnalyticsMeasurementID, gtagOptions: {customer, projectID, userID}}]);
  }
};

const HJID = Number(import.meta.env.VITE_HOTJAR_ID);
const HJSV = Number(import.meta.env.VITE_HOTJAR_SNIPPET_VERSION);

const hotjarScript = `
  (function(h,o,t,j,a,r){
    h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
    h._hjSettings={hjid:${HJID},hjsv:${HJSV}};
    a=o.getElementsByTagName('head')[0];
    r=o.createElement('script');r.async=1;
    r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
    a.appendChild(r);
  })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');
  `;

const googleTagManagerScript = `
  (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
    'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
  })(window,document,'script','dataLayer','${import.meta.env.VITE_GTM_ID ?? ''}');
`;

function injectInlineScript(id: string, content: string) {
  if (typeof document === 'undefined') {
    return () => {};
  }

  const existing = document.getElementById(id);
  if (existing) {
    return () => {};
  }

  const script = document.createElement('script');
  script.id = id;
  script.type = 'text/javascript';
  script.innerHTML = content;
  document.body.appendChild(script);

  return () => {
    script.parentNode?.removeChild(script);
  };
}

export function ThirdPartyScipts() {
  const user = useAppSelector(selectUser);
  const analytics = useAnalytics();
  const router = useRouter();
  const currentProject = useCurrentProjectQuery();
  const tolgee = useTolgee();

  const language = tolgee.getLanguage()?.substring(0, 2) as 'en' | 'de';
  const engagementLocale = tolgee.getLanguage() ?? language ?? 'en';

  // initialize Amplitude Analytics
  useEffect(() => {
    if (!analytics || !user) return;

    analytics.setUserId(user.userId);

    // Create Identify objects for user and group properties
    const userIdentify = new analytics.Identify();
    const groupIdentify = new analytics.Identify();

    // Set user properties
    userIdentify.set('email', user.email);
    if (user.name) userIdentify.set('name', user.name);
    userIdentify.set('customer', user.email.endsWith('maddox.ai') ? `${user.customer}_internal` : user.customer);
    userIdentify.set('project', user.currentProjectId);
    userIdentify.set('language', language ?? 'en');

    analytics.identify(userIdentify);

    // Set group properties
    const customer = user.email.endsWith('maddox.ai') ? `${user.customer}_internal` : user.customer;
    groupIdentify.set('customer', customer);
    analytics.setGroup('customer', customer);
    analytics.setGroup('project', `${customer}_${user.currentProjectId}`);
    analytics.groupIdentify('project', `${customer}_${user.currentProjectId}`, groupIdentify);
  }, [analytics, user, language]);

  useEffect(() => {
    if (!analytics || !window.engagement) return;

    try {
      window.engagement.updateLanguage(engagementLocale);
    } catch {
      // noop
    }
  }, [analytics, engagementLocale]);

  useEffect(() => {
    if (!analytics || !window.engagement) return;

    window.engagement.setRouter((url) => {
      const nextUrl = new URL(url, window.location.origin);

      if (nextUrl.origin !== window.location.origin) {
        window.location.assign(nextUrl.toString());
        return;
      }

      void router.navigate({
        to: `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}` as any,
      });
    });
  }, [analytics, router]);

  // Track page views with Amplitude Analytics
  useEffect(() => {
    const handleRouteChange = (url: string) => {
      if (analytics) analytics.track('Page View', {page: url});
    };

    const unsubscribe = router.subscribe('onBeforeNavigate', (event) => handleRouteChange(event.toLocation.pathname));
    return () => unsubscribe();
    // don't include router as it's not stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analytics]);

  useEffect(() => {
    if (googleAnalyticsMeasurementID) setupGoogleAnalytics(user);
  }, [user]);

  useEffect(() => {
    if (isE2eBuild) {
      return;
    }

    if (user && currentProject.data) {
      const userRole = currentProject.data.users.find((u) => u.email === user.email)?.role;

      Intercom({
        api_base: 'https://api-iam.eu.intercom.io',
        app_id: 'spq1lvzj',
        user_id: user?.userId,
        name: user?.name,
        email: user?.email,
        language_override: language,
        user_role: userRole,
        user_created_at: user.createdAt,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, currentProject.data, language]);

  useEffect(() => {
    if (!includeTelemetry) {
      return;
    }

    const removeHotjar = injectInlineScript('hotjar-script', hotjarScript);
    const removeGtm = injectInlineScript('google-tag-manager', googleTagManagerScript);

    return () => {
      removeHotjar();
      removeGtm();
    };
  }, []);

  return null;
}
